import React, { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { availableToColor } from '../../../../helpers'
import EquipmentStatusChip from '../../EquipmentStatusChip'
import { EquipmentGraphWidget, HistoryAvailabilityGraphWidget } from '../components/Graphs'
import { DetailTable } from '../components/DetailTable'
import { MetricWidget } from '../components/MetricWidget'
import { DetailCardGrid, Widget } from '../components/Widget'
import {
  CCTV_CAMERA_REACHABILITY_GRAPH,
  CCTV_SERVER_CONNECTED_CAMERAS,
  CCTV_SERVER_DISK_GRAPH
} from '../../../../operations-gql/queries'
import {
  buildDiskUsageGraphData,
  formatPercentValue,
  formatUptimeValue,
  getFirstMetricValue
} from '../utils'

const CamerasWidget = ({ equipment }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery(CCTV_SERVER_CONNECTED_CAMERAS, {
    variables: { mac: equipment?.mac, site_id: equipment?.site?.id },
    skip: !equipment?.mac || !equipment?.site?.id
  })
  const cameras = data?.connectedCameras || equipment.connected_cameras || []
  if (loading) {
    return (
      <Widget icon="fa-light fa-camera-cctv" title={t('supervision.cctv.connected_cameras')} sx={{ flex: 1, minHeight: 220 }}>
        <Typography sx={{ color: '#989898', fontSize: 12 }}>{t('ui_elements.loading')}</Typography>
      </Widget>
    )
  }
  return (
    <Widget icon="fa-light fa-camera-cctv" title={t('supervision.cctv.connected_cameras')} sx={{ flex: 1, minHeight: 220 }}>
      <DetailTable
        columns={[
          { key: 'label', label: t('supervision.cctv.label'), width: '60%' },
          { key: 'ip_address', label: t('supervision.card.ip_address'), width: '20%' },
          {
            key: 'connected',
            label: t('supervision.cctv.connection_status'),
            width: '20%',
            align: 'right',
            render: row => (
              <EquipmentStatusChip
                compact
                status={{
                  label: row.connected ? t('supervision.cctv.connected') : t('supervision.cctv.disconnected'),
                  color: row.connected ? '#587F31' : '#DC2626',
                  bgcolor: row.connected ? '#E6F0D7' : '#FEE2E2'
                }}
              />
            )
          },
        ]}
        rows={cameras.map(camera => ({ ...camera, id: camera.mac || camera.label }))}
      />
    </Widget>
  )
}

const CctvCameraSections = ({ equipment }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery(CCTV_CAMERA_REACHABILITY_GRAPH, {
    variables: { mac: equipment?.mac, timeRange: { preset: '-1d' } },
    skip: !equipment?.mac
  })
  const history = data?.cctvCameraHistory || equipment?.history_up
  const cpu = getFirstMetricValue(equipment, ['cpu_usage', 'device_cpu_usage'])
  const memory = getFirstMetricValue(equipment, ['memory_usage', 'device_memory_usage'])
  const uptime = getFirstMetricValue(equipment, ['system_uptime', 'uptime'])
  const snmp = equipment.is_snmp_reachable

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
      <DetailCardGrid sx={{ flex: '0 0 45%', minWidth: 0 }}>
        <MetricWidget title={t('supervision.hardware.ap.cpu')} value={formatPercentValue(cpu)} unit="%" icon="fa-solid fa-microchip" progress={cpu == null ? null : Number(cpu)} showProgress />
        <MetricWidget title={t('supervision.hardware.ap.ram')} value={formatPercentValue(memory)} unit="%" icon="fa-solid fa-memory" progress={memory == null ? null : Number(memory)} showProgress />
        <MetricWidget title={t('supervision.hardware.ap.uptime')} value={formatUptimeValue(uptime, t)} icon="fa-solid fa-clock" />
        <MetricWidget title={t('supervision.hardware.ap.snmp')} value={snmp == null ? null : t(snmp ? 'supervision.hardware.ap.active' : 'supervision.hardware.ap.inactive')} icon="fa-solid fa-network-wired" iconColor={snmp ? availableToColor('up') : '#3D3D3D'} />
        <MetricWidget title={t('supervision.hardware.common.firmware')} value={equipment.firmware_version} icon="fa-solid fa-gear-code" />
      </DetailCardGrid>
      <HistoryAvailabilityGraphWidget
        history={history}
        title={t('supervision.hardware.switch.availability')}
        loading={loading}
        modalQuery={CCTV_CAMERA_REACHABILITY_GRAPH}
        modalVariables={{ mac: equipment?.mac }}
        modalDataKey="cctvCameraHistory"
      />
    </Stack>
  )
}

const CctvDiskUsageGraphWidget = ({ equipment }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery(CCTV_SERVER_DISK_GRAPH, {
    variables: { mac: equipment?.mac, timeRange: { preset: '-1d' } },
    skip: !equipment?.mac
  })
  const graphData = useMemo(() => {
    return buildDiskUsageGraphData(data?.cctvServerGraph)
  }, [data])
  const getDiskName = (index) => {
    const disk = equipment.disks_info?.[String(index)] || equipment.disks_info?.[index - 1]
    return disk?.volume || disk?.label || t(`supervision.hardware.cctv_server.disk_${index}`)
  }
  const hasDiskInfo = equipment.disks_info && Object.keys(equipment.disks_info).length > 0
  const diskSeries = [
    { key: 'disk_1_usage', color: '#A5D9CB', name: getDiskName(1) },
    { key: 'disk_2_usage', color: '#EC7A74', name: getDiskName(2) }
  ].filter((_, index) => !hasDiskInfo || equipment.disks_info?.[String(index + 1)] || equipment.disks_info?.[index])

  return (
    <EquipmentGraphWidget
      title={t('supervision.hardware.cctv_server.disk_usage_graph')}
      data={graphData}
      color="#A5D9CB"
      series={diskSeries}
      unit="%"
      percentage
      showLegend
      loading={loading}
      chartHeight="100%"
      sx={{ height: '100%', minHeight: 300 }}
      modalQuery={CCTV_SERVER_DISK_GRAPH}
      modalVariables={{ mac: equipment?.mac }}
      mapModalData={data => ({ data: buildDiskUsageGraphData(data?.cctvServerGraph), series: diskSeries })}
    />
  )
}

const CctvServerSections = ({ equipment }) => {
  const { t } = useTranslation()
  const cloudConnected = equipment.cloud_connection?.status

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} alignItems="stretch" gap={2}>
      <Stack gap={2} sx={{ flex: '0 0 45%', minWidth: 0 }}>
        <DetailCardGrid>
          <MetricWidget title={t('supervision.cctv.total_disk_usage')} value={formatPercentValue(equipment.disk_usage)} unit="%" icon="fa-solid fa-hard-drive" progress={equipment.disk_usage == null ? null : Number(equipment.disk_usage)} showProgress />
          <MetricWidget
            title={t('supervision.cctv.cloud_status')}
            value={cloudConnected == null ? null : t(cloudConnected ? 'supervision.cctv.connected' : 'supervision.cctv.disconnected')}
            icon="fa-solid fa-cloud"
            iconColor={cloudConnected ? availableToColor('up') : availableToColor('down')}
          />
        </DetailCardGrid>
        <CamerasWidget equipment={equipment} />
      </Stack>
      <Box sx={{ flex: '1 1 55%', minWidth: 0, display: 'flex' }}>
        <CctvDiskUsageGraphWidget equipment={equipment} />
      </Box>
    </Stack>
  )
}

export { CctvCameraSections, CctvServerSections }
